/**
 * Section 8.3's two button treatments, shared so the detail panes stop
 * inventing their own. `w-full` is the default; a button that sits inline
 * overrides width and horizontal padding at the call site.
 */
/**
 * No `w-full` here. It was in the base, and because `w-full` and `w-auto` sit
 * at equal specificity the winner is decided by Tailwind's own source order,
 * where `.w-full` comes after `.w-auto`. So appending `w-auto` at a call site
 * did nothing, and the only escape was an inline `style={{ width: 'auto' }}`.
 * Width is now the call site's business: add `w-full`, `flex-1`, or nothing.
 */
const BTN_BASE =
  'h-12 rounded-[100px] inline-flex items-center justify-center gap-[6px] ' +
  'text-[17px] leading-[22px] font-medium tracking-[-0.43px] ' +
  'cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ios-press ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

export const BTN_PRIMARY = `${BTN_BASE} btn-pri bg-biscayne text-white`;

/**
 * Secondary, inside a white section.
 *
 * The grey wash is a recess: it reads as pressed into the surface it sits on,
 * which is what a secondary action inside a card should look like.
 */
export const BTN_SECONDARY = `${BTN_BASE} btn-sec-wash bg-[rgba(120,120,128,0.16)] text-black`;

/**
 * Secondary, on the page background.
 *
 * Same geometry, opposite construction. On #F2F2F7 the grey wash has nothing
 * to recess into, so it reads as a shape rather than a control.
 *
 * Note what is actually doing the work here, because it is not the fill.
 * Measured, the wash resolves to rgb(222,222,228) on the page background, a
 * contrast of 1.20:1 against it, and on white it resolves to rgb(233,233,235)
 * at 1.21:1. Those are the same. This variant's white fill is 1.12:1, which is
 * LOWER. What separates it from the page is the 1px border at 1.19:1 and the
 * shadow beneath it: the button reads as lifted off the page rather than tinted
 * into it. Elevation, not contrast.
 */
export const BTN_SECONDARY_ON_PAGE =
  `${BTN_BASE} btn-sec-page bg-white text-black border border-[rgba(0,0,0,0.08)] ` +
  'shadow-[0_1px_2px_rgba(0,0,0,0.04)]';

/**
 * 36 high, for a secondary control that is not a full-width button. Section
 * 8.3: "Secondary controls that are not full-width buttons — the sort pill, a
 * segmented track, a search field — use the same rgba(120,120,128,0.16) fill
 * at 36 high." Subhead 15/20, since 17/22 does not sit in 36.
 */
export const BTN_SECONDARY_SM =
  'h-9 px-4 rounded-[100px] inline-flex items-center justify-center gap-1.5 ' +
  'text-[15px] leading-[20px] font-medium btn-sec-wash bg-[rgba(120,120,128,0.16)] text-black ' +
  'whitespace-nowrap cursor-pointer ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

/**
 * The 36-high counterpart to BTN_SECONDARY_ON_PAGE. Six of the page-background
 * secondaries were already the small variant, and pointing them at the 48-high
 * constant would have changed their geometry as well as their fill.
 */
export const BTN_SECONDARY_SM_ON_PAGE =
  'h-9 px-4 rounded-[100px] inline-flex items-center justify-center gap-1.5 ' +
  'text-[15px] leading-[20px] font-medium btn-sec-page bg-white text-black ' +
  'border border-[rgba(0,0,0,0.08)] shadow-[0_1px_2px_rgba(0,0,0,0.04)] ' +
  'whitespace-nowrap cursor-pointer ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

/** 36 high, primary fill. The inline counterpart to BTN_SECONDARY_SM. */
export const BTN_PRIMARY_SM =
  'h-9 px-4 rounded-[100px] inline-flex items-center justify-center gap-1.5 ' +
  'text-[15px] leading-[20px] font-medium btn-pri bg-biscayne text-white ' +
  'whitespace-nowrap cursor-pointer ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';
